/**
 * Devices API — all backend calls for the Devices page.
 *
 * Nurse Call:  sp_dev_getInovonicsDevices        | device_unassign / device_delete
 * Wandering:   getaccutechdevices                | device_unassign / device_delete (shared)
 * System:      sp_dev_getrepeaterbycampus        | system_device_delete
 * Servers:     sp_dev_getPalcareDevicesByCampusID | server_add / server_delete
 */

import { apiFetch, apiTransaction } from '../../../services/api.js';
import { getOffsiteInfo } from '../../../services/session.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all Inovonics / PalCare devices for a campus.
 *
 * V1 procedure: sp_dev_getInovonicsDevices
 * Accepts payload with device_filter, device_category_filter, campus_id.
 */
/**
 * Fetch all Nurse Call (Inovonics / PalCare) devices for a campus.
 */
export function fetchDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: '1,2,3,6,7,8,9,10,11,12,15,16,17',
      campus_id: campusId,
    },
    readOnly,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Unassign (disable) one or more devices.
 *
 * Sets is_active = false, clears resident_id and area_id.
 * V1 TransType: device_unassign
 *
 * @param {Array<object>} devices — array of { device_id, area_id, resident_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function unassignDevices(devices, session) {
  const { offsite, offsite_id } = getOffsiteInfo();

  const payload = devices.map((d) => ({
    device_id: d.device_id,
    is_active: false,
    resident_id: null,
    area_id: null,
    modified_by: session.userId,
    offsite,
    offsite_id,
  }));

  const oldPayload = devices.map((d) => ({
    device_id: d.device_id,
    area_id: d.area_id ?? null,
    resident_id: d.resident_id ?? null,
  }));

  return apiTransaction({
    transType: 'device_unassign',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more devices permanently.
 *
 * V1 TransType: device_delete
 *
 * @param {Array<object>} devices — array of { device_id, area_id, resident_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteDevices(devices, session) {
  const { offsite, offsite_id } = getOffsiteInfo();

  const payload = devices.map((d) => ({
    device_id: d.device_id,
    modified_by: session.userId,
    offsite,
    offsite_id,
  }));

  const oldPayload = devices.map((d) => ({
    device_id: d.device_id,
    area_id: d.area_id ?? null,
    resident_id: d.resident_id ?? null,
  }));

  return apiTransaction({
    transType: 'device_delete',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Wandering (Accutech) devices
// ---------------------------------------------------------------------------

/**
 * Fetch all Accutech / wandering devices for a campus.
 *
 * V1 procedure: getaccutechdevices
 */
export function fetchWanderingDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getaccutechdevices',
    reqType: 'stored',
    payload: { device_filter: '', campus_id: campusId },
    readOnly,
  });
}

// Wandering devices share the same core_device table as Nurse Call.
// Use unassignDevices() and deleteDevices() above for write operations.

// ---------------------------------------------------------------------------
// System devices (Repeaters)
// ---------------------------------------------------------------------------

/**
 * Fetch all system devices (repeaters) for a campus.
 *
 * V1 procedure: sp_dev_getrepeaterbycampus
 */
export function fetchSystemDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getrepeaterbycampus',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Delete one or more system devices permanently.
 *
 * V1 TransType: system_device_delete
 *
 * @param {Array<object>} devices — array of { device_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteSystemDevices(devices, session) {
  const payload = devices.map((d) => ({ device_id: d.device_id }));

  return apiTransaction({
    transType: 'system_device_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Server devices (PalCare servers)
// ---------------------------------------------------------------------------

/**
 * Fetch all PalCare server devices for a campus.
 *
 * V1 procedure: sp_dev_getPalcareDevicesByCampusID
 */
export function fetchServers(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getPalcareDevicesByCampusID',
    reqType: 'stored',
    payload: { device_filter: '', campus_id: campusId },
    readOnly,
  });
}

/**
 * Fetch areas for the Add Server form dropdown.
 *
 * V1 procedure: sp_dev_getareasbycampusid
 */
export function fetchServerAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Add a new server device.
 *
 * V1 TransType: server_add
 *
 * @param {object} serverData — see payload shape in plan
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addServer(serverData, session) {
  return apiTransaction({
    transType: 'server_add',
    payload: [serverData],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete (disable) one or more server devices.
 *
 * V1 TransType: server_delete
 *
 * @param {Array<object>} servers — array of { palcare_device_id, mac_addr }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteServers(servers, session) {
  const payload = servers.map((s) => ({
    palcare_device_id: s.palcare_device_id,
    campus_id: session.campusId,
    mac_addr: s.mac_addr,
  }));

  return apiTransaction({
    transType: 'server_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
